import { c as _c } from "react/compiler-runtime";
import { LazyImage } from "@website/ui/lazy-image";
import type { FC } from "react";
import { useEffect, useState } from "react";
import type { VillaHostFeatureFocusScrollerProperties } from "./villa-host-feature-focus-scroller.types";
const AUTO_SCROLL_INTERVAL_IN_MS = 4000;
const wrap = (value: number, length: number) => {
  if (length === 0) return 0;
  return (value % length + length) % length;
};
const getVisibleItemIndexes = (activeIndex: number, itemCount: number) => {
  const maxVisibleDistance = Math.floor((itemCount - 1) / 2);
  const renderRadius = maxVisibleDistance + 1;
  return Array.from({
    length: renderRadius * 2 + 1
  }, (_, index) => activeIndex - renderRadius + index);
};
const getDistanceStyle = (distance: number, maxVisibleDistance: number) => {
  if (distance === 0 || distance > maxVisibleDistance) {
    return {
      opacity: 0,
      blur: 2.2,
      scale: 0.92
    };
  }
  if (distance === 1) {
    return {
      opacity: 0.68,
      blur: 0.5,
      scale: 0.98
    };
  }
  if (distance === 2) {
    return {
      opacity: 0.42,
      blur: 1.2,
      scale: 0.95
    };
  }
  return {
    opacity: 0.22,
    blur: 2,
    scale: 0.92
  };
};
const getItemOffset = (circularOffset: number) => {
  const distance = Math.abs(circularOffset);
  if (distance === 0) return 0;

  // Space between active item and nearest items
  const spacingFromActive = 72;

  // Space between inactive items
  const spacingBetweenItems = 36;
  return circularOffset > 0 ? spacingFromActive + (distance - 1) * spacingBetweenItems : -(spacingFromActive + (distance - 1) * spacingBetweenItems);
};
const titlePositionClassName = "absolute left-1/2 top-1/2 min-w-max -translate-y-1/2 text-left max-lg:-translate-x-[60%] lg:-translate-x-1/2";
const Component: FC<VillaHostFeatureFocusScrollerProperties> = t0 => {
  const $ = _c(27);
  const {
    features
  } = t0;
  const itemCount = features.length;
  const [activeIndex, setActiveIndex] = useState(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = direction => {
      setActiveIndex(previous => previous + direction);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const move = t1;
  let t2;
  let t3;
  if ($[1] !== itemCount) {
    t2 = () => {
      if (itemCount === 0) {
        return;
      }
      setActiveIndex(previous_0 => wrap(previous_0, itemCount));
    };
    t3 = [itemCount];
    $[1] = itemCount;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== itemCount) {
    t4 = () => {
      if (itemCount <= 1) {
        return;
      }
      const intervalId = window.setInterval(() => {
        move(1);
      }, AUTO_SCROLL_INTERVAL_IN_MS);
      return () => window.clearInterval(intervalId);
    };
    t5 = [itemCount, move];
    $[4] = itemCount;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const activeFeature = itemCount > 0 ? features[wrap(activeIndex, itemCount)] : null;
  const maxVisibleDistance = Math.floor((itemCount - 1) / 2);
  let t10;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[7] !== activeIndex || $[8] !== features || $[9] !== itemCount || $[10] !== maxVisibleDistance) {
    const visibleItemIndexes = itemCount > 0 ? getVisibleItemIndexes(activeIndex, itemCount) : [];
    t8 = "\u0648\u06CC\u0698\u06AF\u06CC\u200C\u0647\u0627\u06CC \u067E\u0646\u0644 \u0645\u06CC\u0632\u0628\u0627\u0646";
    t9 = "relative mx-auto h-[400px] w-full max-w-[560px] overflow-hidden rounded-[28px] text-center outline-none";
    t10 = "rtl";
    t6 = "relative z-10 h-full";
    t7 = visibleItemIndexes.map(virtualIndex => {
      const item = features[wrap(virtualIndex, itemCount)];
      const offsetFromActive = virtualIndex - activeIndex;
      const distance = Math.abs(offsetFromActive);
      const offset = getItemOffset(offsetFromActive);
      const {
        opacity,
        blur,
        scale
      } = getDistanceStyle(distance, maxVisibleDistance);
      return <li className="absolute inset-x-0 h-8 px-4 transition-[opacity,filter,transform] duration-[560ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[opacity,filter,transform] sm:px-6" key={virtualIndex} style={{
        opacity,
        filter: `blur(${blur}px)`,
        top: "50%",
        transform: `translate3d(0, calc(-50% + ${offset}px), 0) scale(${scale})`
      }}><p className={`${titlePositionClassName} text-base font-bold`}>{item.title}</p></li>;
    });
    $[7] = activeIndex;
    $[8] = features;
    $[9] = itemCount;
    $[10] = maxVisibleDistance;
    $[11] = t10;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t10 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  let t11;
  if ($[16] !== t6 || $[17] !== t7) {
    t11 = <ul className={t6}>{t7}</ul>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== activeFeature) {
    t12 = activeFeature && <div className="absolute inset-x-0 top-1/2 z-20 m-auto w-[98%] -translate-y-1/2 lg:w-[90%] lg:px-6"><div className="border-outline-thin relative h-16 overflow-hidden rounded-[20px] border bg-white px-4 lg:px-6" style={{
        boxShadow: "0px 3px 3px -2px #00000033, 0px 3px 4px 0px #00000024, 0px 1px 8px 0px #0000001F"
      }}><div className="absolute inset-x-0 top-1/2 flex h-16 -translate-y-1/2 items-center"><span className="flex items-center justify-center pr-6"><LazyImage src={activeFeature.icon} className="!size-11 max-h-full max-w-full lg:!size-14" /></span><p className={`text-on-surface-high-emphasis ${titlePositionClassName} text-base font-bold lg:text-xl`}>{activeFeature.title}</p></div></div></div>;
    $[19] = activeFeature;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t12 || $[24] !== t8 || $[25] !== t9) {
    t13 = <div aria-label={t8} className={t9} dir={t10}>{t11}{t12}</div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t8;
    $[25] = t9;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
};
export const VillaHostFeatureFocusScroller = Component;
function _temp() {
  return 4;
}